// Navbar.tsx
import { useState, useRef, useEffect } from "react";
import {
  MapPinIcon,
  HomeIcon,
  ChartBarIcon,
  ShoppingCartIcon,
  CurrencyDollarIcon,
  UsersIcon,
  BuildingStorefrontIcon,
  ClipboardDocumentListIcon,
  UserIcon,
  KeyIcon,
  EnvelopeIcon,
  EllipsisHorizontalCircleIcon,
  ArrowLeftOnRectangleIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "../../auth/useAuth";
import ActionDialog from "../../components/common/ActionDialog";
import SmvsPrint from "../../prints/SmvsPrint";
import SalesPrint from "../../prints/SalesPrint";
import TxnsPrint from "../../prints/TxnsPrint";
import POrdersPrint from "../../prints/POrdersPrint";
import PVouchersPrint from "../../prints/PVouchersPrint";
import InvoicesPrint from "../../prints/InvoicesPrint";
import RVouchersPrint from "../../prints/RVouchersPrint";

export default function Navbar() {
  const { data, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const user = data?.user;
  const branch = data?.branch?.name || null;
  const isAdmin = data?.isAdmin;
  const isPower = data?.isPower;
  const isSuper = data?.isSuper;
  const isStaff = data?.isStaff;

  const [isOpen, setIsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [activeMobileSection, setActiveMobileSection] = useState<
    "sell" | "stock" | "finance" | "reports" | "settings" | "account" | null
  >(null);
  const [sellDropdown, setSellDropdown] = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);
  const [reportsDropdown, setReportsDropdown] = useState(false);
  const [stockDropdown, setStockDropdown] = useState(false);
  const [billingDropdown, setBillingDropdown] = useState(false);
  const [smvsOpen, setSmvsOpen] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);
  const [txnsOpen, setTxnsOpen] = useState(false);
  const [pordersOpen, setPordersOpen] = useState(false);
  const [pvouchersOpen, setPvouchersOpen] = useState(false);
  const [invoicesOpen, setInvoicesOpen] = useState(false);
  const [rvouchersOpen, setRvouchersOpen] = useState(false);
  const [adminDropdown, setAdminDropdown] = useState(false);
  const [sellDropdownUp, setSellDropdownUp] = useState(false);
  const [stockDropdownUp, setStockDropdownUp] = useState(false);
  const [billingDropdownUp, setBillingDropdownUp] = useState(false);
  const [reportsDropdownUp, setReportsDropdownUp] = useState(false);
  const [userDropdownUp, setUserDropdownUp] = useState(false);

  const dropdownRef = useRef<HTMLLIElement>(null);
  const adminRef = useRef<HTMLLIElement>(null);
  const reportsRef = useRef<HTMLLIElement>(null);
  const sellRef = useRef<HTMLLIElement>(null);
  const stockRef = useRef<HTMLLIElement>(null);
  const billingRef = useRef<HTMLLIElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeMobileMenu = () => setIsOpen(false);

  const shouldFlipDropdown = (
    parentRef: React.RefObject<HTMLLIElement | null>,
  ) => {
    if (!parentRef.current) return false;
    const rect = parentRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    // goal: use the larger available side when the menu is long
    return spaceBelow < 240 && spaceAbove > spaceBelow;
  };

  const updateDropdownDirections = () => {
    if (sellRef.current) setSellDropdownUp(shouldFlipDropdown(sellRef));
    if (stockRef.current) setStockDropdownUp(shouldFlipDropdown(stockRef));
    if (billingRef.current)
      setBillingDropdownUp(shouldFlipDropdown(billingRef));
    if (reportsRef.current)
      setReportsDropdownUp(shouldFlipDropdown(reportsRef));
    if (dropdownRef.current) setUserDropdownUp(shouldFlipDropdown(dropdownRef));
  };

  useEffect(() => {
    if (
      sellDropdown ||
      stockDropdown ||
      billingDropdown ||
      reportsDropdown ||
      userDropdown
    ) {
      updateDropdownDirections();
    }
  }, [
    sellDropdown,
    stockDropdown,
    billingDropdown,
    reportsDropdown,
    userDropdown,
  ]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setUserDropdown(false);
      }
      if (
        adminRef.current &&
        !adminRef.current.contains(event.target as Node)
      ) {
        setAdminDropdown(false);
      }
      if (
        reportsRef.current &&
        !reportsRef.current.contains(event.target as Node)
      ) {
        setReportsDropdown(false);
      }
      if (sellRef.current && !sellRef.current.contains(event.target as Node)) {
        setSellDropdown(false);
      }
      if (
        stockRef.current &&
        !stockRef.current.contains(event.target as Node)
      ) {
        setStockDropdown(false);
      }
      if (
        billingRef.current &&
        !billingRef.current.contains(event.target as Node)
      ) {
        setBillingDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (isLoading) {
    return <nav className="p-4 border-b border-gray-300">Loading...</nav>;
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-gray-300 bg-white shadow-sm">
      <div
        ref={menuRef}
        className="max-w-screen-xl flex flex-wrap items-center justify-between mx-auto p-4"
      >
        {/* Logo */}
        <Link
          to={user ? "/home" : "/"}
          className="flex items-center space-x-3 font-bold text-lg"
          onClick={() => {
            if (isOpen) {
              setIsOpen(false);
            }
          }}
        >
          <img
            src="/osh-logo.png"
            alt="OurShopHub logo"
            className="w-8 h-8 object-contain"
          />
          <span>OurShopHub</span>
        </Link>

        {/* Hamburger / Close toggle */}
        {user ? (
          <button
            className="inline-flex items-center p-2 w-10 h-10 justify-center text-gray-700 rounded-md md:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
            onClick={() => {
              setMobileMenuOpen((open) => {
                const next = !open;
                if (!next) {
                  setActiveMobileSection(null);
                }
                return next;
              });
              setIsOpen(false);
            }}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            <span className="sr-only">
              {mobileMenuOpen ? "Close menu" : "Open menu"}
            </span>
            {mobileMenuOpen ? (
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        ) : (
          <Link
            to="/login"
            className="md:hidden inline-flex items-center gap-2 font-bold text-gray-700 hover:text-teal-800"
          >
            <UserIcon className="w-5 h-5" /> Login
          </Link>
        )}

        {/* Desktop top menu (hide on mobile, mobile is via bottom app bar and section panel) */}
        <div className="hidden md:block md:static md:w-auto md:max-w-none md:shadow-none md:bg-transparent">
          {/* Close button (mobile only) */}
          <div className="flex items-center justify-between p-4 md:hidden border-b border-gray-200">
            <span className="font-bold text-lg">Menu</span>
            <button
              className="p-2 rounded hover:bg-gray-100 focus:outline-none"
              onClick={() => setIsOpen(false)}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <ul className="flex flex-col md:flex-row md:space-x-6 font-medium p-4 md:p-0 bg-white md:bg-transparent rounded-lg md:rounded-none border-0 md:border-0 text-2xl md:text-sm">
            {/* Show Home if logged in */}
            {user && (
              <>
                <li>
                  <Link
                    to="/home"
                    className="block py-2 px-3 rounded font-bold hover:text-blue-600 md:p-0 flex items-center gap-2"
                    onClick={closeMobileMenu}
                  >
                    <HomeIcon className="w-5 h-5 text-[#069494]" /> Home
                  </Link>
                </li>
                {/* Sell Dashboard link + Dropdown */}
                <li ref={sellRef} className="relative block py-2 px-3 md:p-0">
                  <div className="flex items-center gap-1">
                    {/* Sell label and dropdown toggle only */}
                    <span className="flex items-center text-gray-700 gap-2 font-bold select-none">
                      <ShoppingCartIcon className="w-5 h-5 text-[#069494]" />
                      <span className="text-gray-700">Sell</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setSellDropdown((prev) => !prev)}
                      className="flex items-center text-gray-700 hover:text-blue-600 focus:outline-none"
                    >
                      <svg
                        className="w-4 h-4 ml-1 transition-transform duration-200"
                        style={{
                          transform: sellDropdown
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>
                  {/* Dropdown */}
                  <ul
                    className={`absolute mt-2 bg-gradient-to-br from-[#069494] via-[#ffce1b] to-[#b7410e] border border-transparent rounded-md shadow-md z-20 transform origin-top transition-all duration-200 ease-out backdrop-blur-md max-h-[70vh] overflow-y-auto
                      ${
                        sellDropdown
                          ? "opacity-100 scale-100"
                          : "opacity-0 scale-95 pointer-events-none"
                      }
                      left-0 right-0 md:right-0 md:left-auto`}
                    style={{
                      maxHeight: "calc(100vh - 4rem)",
                      top: sellDropdownUp ? "auto" : "100%",
                      bottom: sellDropdownUp ? "100%" : "auto",
                    }}
                  >
                    {/* Dashboard link for isPower and isSuper only */}
                    {(isPower || isSuper) && (
                      <li>
                        <Link
                          to="/daily-sales"
                          className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2 font-semibold"
                          onClick={() => {
                            setSellDropdown(false);
                            closeMobileMenu();
                          }}
                        >
                          <HomeIcon className="w-5 h-5 text-[#069494]" />{" "}
                          Dashboard
                        </Link>
                      </li>
                    )}
                    <li>
                      <Link
                        to="/sales"
                        className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                        onClick={() => {
                          setSellDropdown(false);
                          closeMobileMenu();
                        }}
                      >
                        <ShoppingCartIcon className="w-5 h-5 text-[#069494]" />{" "}
                        Sale
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/transactions"
                        className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                        onClick={() => {
                          setSellDropdown(false);
                          closeMobileMenu();
                        }}
                      >
                        <CurrencyDollarIcon className="w-5 h-5 text-[#069494]" />{" "}
                        Transact
                      </Link>
                    </li>

                    <li>
                      <Link
                        to="/customers"
                        className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                        onClick={() => {
                          setSellDropdown(false);
                          closeMobileMenu();
                        }}
                      >
                        <UserIcon className="w-5 h-5 text-[#069494]" />{" "}
                        Customers
                      </Link>
                    </li>
                  </ul>
                </li>

                {/* Stock Dashboard link + Dropdown */}
                <li ref={stockRef} className="relative block py-2 px-3 md:p-0">
                  <div className="flex items-center gap-1">
                    {/* Stock label and dropdown toggle only */}
                    <span className="flex items-center text-gray-700 gap-2 font-bold select-none">
                      <ClipboardDocumentListIcon className="w-5 h-5 text-[#069494]" />
                      <span className="text-gray-700">Stock</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setStockDropdown((prev) => !prev)}
                      className="flex items-center text-gray-700 hover:text-blue-600 focus:outline-none"
                    >
                      <svg
                        className="w-4 h-4 ml-1 transition-transform duration-200"
                        style={{
                          transform: stockDropdown
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>

                  <ul
                    className={`absolute mt-2 bg-gradient-to-br from-[#069494] via-[#ffce1b] to-[#b7410e] border border-transparent rounded-md shadow-md z-20 transform origin-top transition-all duration-200 ease-out backdrop-blur-md
                      ${
                        stockDropdown
                          ? "opacity-100 scale-100"
                          : "opacity-0 scale-95 pointer-events-none"
                      }
                      left-0 right-0 md:right-0 md:left-auto`}
                    style={{
                      top: stockDropdownUp ? "auto" : "100%",
                      bottom: stockDropdownUp ? "100%" : "auto",
                      maxHeight: "calc(100vh - 4rem)",
                    }}
                  >
                    {/* Dashboard link for isPower and isSuper only */}
                    {(isPower || isSuper) && (
                      <li>
                        <Link
                          to="/inventory-value"
                          className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2 font-semibold"
                          onClick={() => {
                            setStockDropdown(false);
                            closeMobileMenu();
                          }}
                        >
                          <HomeIcon className="w-5 h-5 text-[#069494]" />{" "}
                          Dashboard
                        </Link>
                      </li>
                    )}
                    <li>
                      <Link
                        to="/stock-movement"
                        className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                        onClick={() => {
                          setStockDropdown(false);
                          closeMobileMenu();
                        }}
                      >
                        <ClipboardDocumentListIcon className="w-5 h-5 text-[#069494]" />{" "}
                        Stock
                      </Link>
                    </li>
                    {isSuper && (
                      <li>
                        <Link
                          to="/products"
                          className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                          onClick={() => {
                            setStockDropdown(false);
                            closeMobileMenu();
                          }}
                        >
                          <BuildingStorefrontIcon className="w-5 h-5 text-[#069494]" />{" "}
                          Products
                        </Link>
                      </li>
                    )}
                    <li>
                      <Link
                        to="/suppliers"
                        className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                        onClick={() => {
                          setStockDropdown(false);
                          closeMobileMenu();
                        }}
                      >
                        <UsersIcon className="w-5 h-5 text-[#069494]" />{" "}
                        Suppliers
                      </Link>
                    </li>
                  </ul>
                </li>
                {/* Finance Dropdown (Admin only) - moved after Stock */}
                {(isAdmin || isPower || isSuper) && (
                  <li
                    ref={billingRef}
                    className="relative block py-2 px-3 md:p-0"
                  >
                    <button
                      onClick={() => setBillingDropdown((prev) => !prev)}
                      className="flex items-center text-gray-700 hover:text-blue-600 focus:outline-none gap-2 font-bold"
                    >
                      <CurrencyDollarIcon className="w-5 h-5 text-[#069494]" />
                      <span className="text-gray-700">Finance</span>
                      <svg
                        className="w-4 h-4 ml-1 transition-transform duration-200"
                        style={{
                          transform: billingDropdown
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    <ul
                      className={`absolute mt-2 bg-gradient-to-br from-[#069494] via-[#ffce1b] to-[#b7410e] border border-transparent rounded-md shadow-md z-20 transform origin-top transition-all duration-200 ease-out backdrop-blur-md
                      ${
                        billingDropdown
                          ? "opacity-100 scale-100"
                          : "opacity-0 scale-95 pointer-events-none"
                      }
                      left-0 right-0 md:right-0 md:left-auto`}
                      style={{
                        top: billingDropdownUp ? "auto" : "100%",
                        bottom: billingDropdownUp ? "100%" : "auto",
                        maxHeight: "calc(100vh - 4rem)",
                      }}
                    >
                      <li>
                        <Link
                          to="/purchase-orders"
                          className="block px-4 py-2 font-normal text-gray-700 font-normal hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                          onClick={() => {
                            setBillingDropdown(false);
                            closeMobileMenu();
                          }}
                        >
                          <ShoppingCartIcon className="w-5 h-5 text-[#069494]" />{" "}
                          Purch~Order
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/pay-voucher"
                          className="block px-4 py-2 font-normal text-gray-700 font-normal hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                          onClick={() => {
                            setBillingDropdown(false);
                            closeMobileMenu();
                          }}
                        >
                          <CurrencyDollarIcon className="w-5 h-5 text-[#069494]" />{" "}
                          Pay~Voucher
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/invoices"
                          className="block px-4 py-2 font-normal text-gray-700 font-normal hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                          onClick={() => {
                            setBillingDropdown(false);
                            closeMobileMenu();
                          }}
                        >
                          <CurrencyDollarIcon className="w-5 h-5 text-[#069494]" />{" "}
                          Invoice
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/receipt-voucher"
                          className="block px-4 py-2 font-normal text-gray-700 font-normal hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                          onClick={() => {
                            setBillingDropdown(false);
                            closeMobileMenu();
                          }}
                        >
                          <CurrencyDollarIcon className="w-5 h-5 text-[#069494]" />{" "}
                          Receipt
                        </Link>
                      </li>
                    </ul>
                  </li>
                )}
              </>
            )}

            {/* Reports Dropdown */}
            {user && (
              <li ref={reportsRef} className="relative block py-2 px-3 md:p-0">
                <button
                  onClick={() => setReportsDropdown((prev) => !prev)}
                  className="flex items-center text-gray-700 hover:text-blue-600 focus:outline-none gap-2 font-bold"
                >
                  <ClipboardDocumentListIcon className="w-5 h-5 text-[#069494]" />
                  <span className="text-gray-700">Reports</span>
                  <svg
                    className="w-4 h-4 ml-1 transition-transform duration-200"
                    style={{
                      transform: reportsDropdown
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Dropdown */}
                <ul
                  className={`absolute mt-2 bg-gradient-to-br from-[#069494] via-[#ffce1b] to-[#b7410e] border border-transparent rounded-md shadow-md z-20 transform origin-top transition-all duration-200 ease-out backdrop-blur-md max-h-[70vh] overflow-y-auto
                    ${
                      reportsDropdown
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-95 pointer-events-none"
                    }
                    left-0 right-0 md:right-0 md:left-auto`}
                  style={{
                    top: reportsDropdownUp ? "auto" : "100%",
                    bottom: reportsDropdownUp ? "100%" : "auto",
                    maxHeight: "calc(100vh - 4rem)",
                  }}
                >
                  <li>
                    <button
                      type="button"
                      className="block w-full text-left px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2 cursor-pointer"
                      onClick={() => {
                        setReportsDropdown(false);
                        setTxnsOpen(true);
                        closeMobileMenu();
                      }}
                    >
                      <ClipboardDocumentListIcon className="w-5 h-5 text-[#069494]" />{" "}
                      TXNs
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      className="block w-full text-left px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2 cursor-pointer"
                      onClick={() => {
                        setReportsDropdown(false);
                        setSalesOpen(true);
                        closeMobileMenu();
                      }}
                    >
                      <ClipboardDocumentListIcon className="w-5 h-5 text-[#069494]" />{" "}
                      Sales
                    </button>
                  </li>
                  {/* Available to power users and super users */}
                  {(isPower || isSuper) && (
                    <>
                      <li>
                        <button
                          type="button"
                          className="block w-full text-left px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2 cursor-pointer"
                          onClick={() => {
                            setReportsDropdown(false);
                            setSmvsOpen(true);
                            closeMobileMenu();
                          }}
                        >
                          <ClipboardDocumentListIcon className="w-5 h-5 text-[#069494]" />{" "}
                          SMVs
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="block w-full text-left px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2 cursor-pointer"
                          onClick={() => {
                            setReportsDropdown(false);
                            setPordersOpen(true);
                            closeMobileMenu();
                          }}
                        >
                          <ClipboardDocumentListIcon className="w-5 h-5 text-[#069494]" />{" "}
                          P~Orders
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="block w-full text-left px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2 cursor-pointer"
                          onClick={() => {
                            setReportsDropdown(false);
                            setPvouchersOpen(true);
                            closeMobileMenu();
                          }}
                        >
                          <ClipboardDocumentListIcon className="w-5 h-5 text-[#069494]" />{" "}
                          P~Vouchers
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="block w-full text-left px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2 cursor-pointer"
                          onClick={() => {
                            setReportsDropdown(false);
                            setInvoicesOpen(true);
                            closeMobileMenu();
                          }}
                        >
                          <ClipboardDocumentListIcon className="w-5 h-5 text-[#069494]" />{" "}
                          Invoices
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="block w-full text-left px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2 cursor-pointer"
                          onClick={() => {
                            setReportsDropdown(false);
                            setRvouchersOpen(true);
                            closeMobileMenu();
                          }}
                        >
                          <ClipboardDocumentListIcon className="w-5 h-5 text-[#069494]" />{" "}
                          R-Vouchers
                        </button>
                      </li>
                    </>
                  )}
                </ul>
              </li>
            )}

            {/* Settings Dropdown */}
            {user && (isPower || isSuper) && (
              <li ref={adminRef} className="relative block py-2 px-3 md:p-0">
                <button
                  onClick={() => setAdminDropdown(!adminDropdown)}
                  className="flex items-center text-gray-700 hover:text-blue-600 focus:outline-none gap-2 font-bold"
                >
                  <Cog6ToothIcon className="w-5 h-5 text-[#069494]" />
                  <span className="text-gray-700">Settings</span>
                  <svg
                    className="w-4 h-4 ml-1 transition-transform duration-200"
                    style={{
                      transform: adminDropdown
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Dropdown */}
                <ul
                  className={`absolute mt-2 bg-gradient-to-br from-[#069494] via-[#ffce1b] to-[#b7410e] border border-transparent rounded-md shadow-md z-20 transform origin-top transition-all duration-200 ease-out backdrop-blur-md max-h-[70vh] overflow-y-auto
                    ${
                      adminDropdown
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-95 pointer-events-none"
                    }
                    left-0 right-0 md:right-0 md:left-auto`}
                  style={{ maxHeight: "calc(100vh - 4rem)" }}
                >
                  {/* Stock, Products and Suppliers moved to the Stock menu */}
                  {/* Billing items moved to Billing menu */}
                  <li>
                    <Link
                      to="/users"
                      className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                      onClick={() => {
                        setAdminDropdown(false);
                        closeMobileMenu();
                      }}
                    >
                      <UsersIcon className="w-5 h-5 text-[#069494]" /> Users
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/promotions"
                      className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                      onClick={() => {
                        setAdminDropdown(false);
                        closeMobileMenu();
                      }}
                    >
                      <CalendarDaysIcon className="w-5 h-5 text-[#069494]" />
                      Promotions
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/accounting-period"
                      className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                      onClick={() => {
                        setAdminDropdown(false);
                        closeMobileMenu();
                      }}
                    >
                      <CalendarDaysIcon className="w-5 h-5 text-[#069494]" />{" "}
                      A/C~Period
                    </Link>
                  </li>
                  {isPower && isStaff && (
                    <li>
                      <Link
                        to="/items-crud"
                        className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                        onClick={() => {
                          setAdminDropdown(false);
                          closeMobileMenu();
                        }}
                      >
                        <Squares2X2Icon className="w-5 h-5 text-[#069494]" />{" "}
                        Items
                      </Link>
                    </li>
                  )}
                  {isSuper && isStaff && (
                    <>
                      <li>
                        <Link
                          to="/models-crud"
                          className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                          onClick={() => {
                            setAdminDropdown(false);
                            closeMobileMenu();
                          }}
                        >
                          <Squares2X2Icon className="w-5 h-5 text-[#069494]" />{" "}
                          Models
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/categories-crud"
                          className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                          onClick={() => {
                            setAdminDropdown(false);
                            closeMobileMenu();
                          }}
                        >
                          <Squares2X2Icon className="w-5 h-5 text-[#069494]" />{" "}
                          Categories
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/collections-crud"
                          className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                          onClick={() => {
                            setAdminDropdown(false);
                            closeMobileMenu();
                          }}
                        >
                          <Squares2X2Icon className="w-5 h-5 text-[#069494]" />{" "}
                          Collections
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/brands-crud"
                          className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                          onClick={() => {
                            setAdminDropdown(false);
                            closeMobileMenu();
                          }}
                        >
                          <Squares2X2Icon className="w-5 h-5 text-[#069494]" />{" "}
                          Brands
                        </Link>
                      </li>
                    </>
                  )}
                </ul>
              </li>
            )}

            {/* User Dropdown */}
            {user && (
              <li ref={dropdownRef} className="relative block py-2 px-3 md:p-0">
                <button
                  onClick={() => setUserDropdown(!userDropdown)}
                  className="flex items-center text-gray-700 hover:text-teal-800 focus:outline-none gap-2 font-bold"
                >
                  <UserIcon className="w-5 h-5 text-[#069494]" />
                  {user.charAt(0).toUpperCase() + user.slice(1)}
                  <svg
                    className="w-4 h-4 ml-1 transition-transform duration-200"
                    style={{
                      transform: userDropdown
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                <div className="absolute top-full right-0 mt-1 text-md text-gray-400 font-medium font-semibold whitespace-nowrap z-30 tracking-widest flex items-center gap-1"></div>

                {/* Dropdown */}
                <ul
                  className={`absolute mt-2 bg-gradient-to-br from-[#069494] via-[#ffce1b] to-[#b7410e] border border-transparent rounded-md shadow-md z-20 transform origin-top transition-all duration-200 ease-out backdrop-blur-md max-h-[70vh] overflow-y-auto
                    ${
                      userDropdown
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-95 pointer-events-none"
                    }
                    left-0 right-0 md:right-0 md:left-auto`}
                  style={{
                    top: userDropdownUp ? "auto" : "100%",
                    bottom: userDropdownUp ? "100%" : "auto",
                    maxHeight: "calc(100vh - 4rem)",
                  }}
                >
                  <li>
                    <Link
                      to="/email-change"
                      className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                      onClick={() => {
                        setUserDropdown(false);
                        closeMobileMenu();
                      }}
                    >
                      <EnvelopeIcon className="w-5 h-5 text-[#069494]" /> Email
                      Change
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/password-change"
                      className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                      onClick={() => {
                        setUserDropdown(false);
                        closeMobileMenu();
                      }}
                    >
                      <KeyIcon className="w-5 h-5 text-[#069494]" /> Password
                      Change
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/code-change"
                      className="block px-4 py-2 font-normal text-gray-700 hover:bg-gray-100 whitespace-nowrap flex items-center gap-2"
                      onClick={() => {
                        setUserDropdown(false);
                        closeMobileMenu();
                      }}
                    >
                      <KeyIcon className="w-5 h-5 text-[#069494]" /> PIN Change
                    </Link>
                  </li>
                  <li>
                    <button
                      onClick={() => {
                        setLogoutDialogOpen(true);
                        setUserDropdown(false);
                        closeMobileMenu();
                      }}
                      className="w-full text-left px-4 py-2 text-red-600 font-bold rounded hover:bg-gray-100 whitespace-nowrap cursor-pointer flex items-center gap-2"
                    >
                      <ArrowLeftOnRectangleIcon className="w-5 h-5 text-red-600" />{" "}
                      Logout
                    </button>
                  </li>
                </ul>
                {/* Branch name below dropdown */}
                {user && branch && (
                  <div className="mt-2 flex items-center gap-2 text-sm font-semibold text-teal-700">
                    <MapPinIcon className="w-4 h-4" aria-hidden="true" />
                    {branch.toUpperCase().charAt(0) +
                      branch.toLowerCase().slice(1) +
                      " Branch"}
                  </div>
                )}
              </li>
            )}

            {/* If NOT logged in → show login */}
            {!user && (
              <li>
                <Link
                  to="/login"
                  className="block py-2 px-3 rounded font-extrabold text-gray-700 hover:text-teal-800 md:p-0 flex items-center gap-2"
                  onClick={closeMobileMenu}
                >
                  <UserIcon className="w-5 h-5" /> Login
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>
      {/* SMVs, Sales and TXNs modals */}
      <SmvsPrint open={smvsOpen} onClose={() => setSmvsOpen(false)} />
      <SalesPrint open={salesOpen} onClose={() => setSalesOpen(false)} />
      <TxnsPrint open={txnsOpen} onClose={() => setTxnsOpen(false)} />
      <POrdersPrint open={pordersOpen} onClose={() => setPordersOpen(false)} />
      <PVouchersPrint
        open={pvouchersOpen}
        onClose={() => setPvouchersOpen(false)}
      />
      <InvoicesPrint
        open={invoicesOpen}
        onClose={() => setInvoicesOpen(false)}
      />
      <RVouchersPrint
        open={rvouchersOpen}
        onClose={() => setRvouchersOpen(false)}
      />

      <ActionDialog
        isOpen={logoutDialogOpen}
        title="Confirm Logout"
        message="Are you sure you want to log out?"
        actionColor="#dc2626"
        proceedLabel="Logout"
        cancelLabel="Cancel"
        onProceed={() => {
          setLogoutDialogOpen(false);
          logout();
          navigate("/");
        }}
        onCancel={() => setLogoutDialogOpen(false)}
      />

      {/* Mobile-first bottom app bar */}
      {user && (
        <div
          className={`${mobileMenuOpen ? "fixed" : "hidden"} md:hidden inset-x-0 bottom-0 z-50 bg-white border-t border-gray-200 shadow-lg`}
        >
          <div className="grid grid-flow-col auto-cols-fr gap-0 text-[10px] text-gray-700">
            <button
              className="py-2 hover:bg-gray-100 flex flex-col items-center justify-center"
              onClick={() => {
                navigate("/home");
                setMobileMenuOpen(false);
              }}
            >
              <HomeIcon className="w-5 h-5" />
              Home
            </button>
            <button
              className="py-2 hover:bg-gray-100 flex flex-col items-center justify-center"
              onClick={() => {
                setMobileMenuOpen(true);
                setActiveMobileSection("sell");
              }}
            >
              <ShoppingCartIcon className="w-5 h-5" />
              Sell
            </button>
            <button
              className="py-2 hover:bg-gray-100 flex flex-col items-center justify-center"
              onClick={() => {
                setMobileMenuOpen(true);
                setActiveMobileSection("stock");
              }}
            >
              <ClipboardDocumentListIcon className="w-5 h-5" />
              Stock
            </button>
            {(isAdmin || isPower || isSuper) && (
              <button
                className="py-2 hover:bg-gray-100 flex flex-col items-center justify-center"
                onClick={() => {
                  setMobileMenuOpen(true);
                  setActiveMobileSection("finance");
                }}
              >
                <CurrencyDollarIcon className="w-5 h-5" />
                Finance
              </button>
            )}
            <button
              className="py-2 hover:bg-gray-100 flex flex-col items-center justify-center"
              onClick={() => {
                setMobileMenuOpen(true);
                setActiveMobileSection("reports");
              }}
            >
              <ClipboardDocumentListIcon className="w-5 h-5" />
              Reports
            </button>
            {isPower && (
              <button
                className="py-2 hover:bg-gray-100 flex flex-col items-center justify-center"
                onClick={() => {
                  setMobileMenuOpen(true);
                  setActiveMobileSection("settings");
                }}
              >
                <Cog6ToothIcon className="w-5 h-5" />
                Settings
              </button>
            )}
            <button
              className="py-2 hover:bg-gray-100 flex flex-col items-center justify-center"
              onClick={() => {
                setMobileMenuOpen(true);
                setActiveMobileSection("account");
              }}
            >
              <UserIcon className="w-5 h-5" />
              Me
            </button>
          </div>
        </div>
      )}

      {user && mobileMenuOpen && activeMobileSection && (
        <div className="fixed inset-0 z-40 bg-black/35 md:hidden">
          <div className="absolute bottom-14 left-0 right-0 bg-white rounded-t-xl shadow-2xl max-h-[65vh] animate-slide-up pb-24">
            <div className="sticky top-0 z-20 bg-white border-b border-gray-200">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="font-semibold text-gray-800 text-sm uppercase">
                  {activeMobileSection === "sell" && "Sell"}
                  {activeMobileSection === "stock" && "Stock"}
                  {activeMobileSection === "finance" && "Finance"}
                  {activeMobileSection === "reports" && "Reports"}
                  {activeMobileSection === "settings" && "Settings"}
                  {activeMobileSection === "account" && "Account"}
                </span>
                <button
                  className="text-gray-600 hover:text-gray-700"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setActiveMobileSection(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(65vh-56px)]">
              {activeMobileSection === "sell" && (
                <div className="grid grid-cols-3 gap-2">
                  {(isPower || isSuper) && (
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate("/daily-sales");
                      }}
                      className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                    >
                      <ChartBarIcon className="w-10 h-10 text-[#069494]" />
                      <span className="text-[10px] mt-1">Dashboard</span>
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/sales");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <ShoppingCartIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">Sales</span>
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/transactions");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <CurrencyDollarIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">Transactions</span>
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/customers");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <UserIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">Customers</span>
                  </button>
                </div>
              )}
              {activeMobileSection === "stock" && (
                <div className="grid grid-cols-3 gap-2">
                  {(isPower || isSuper) && (
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate("/inventory-value");
                      }}
                      className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                    >
                      <ChartBarIcon className="w-10 h-10 text-[#069494]" />
                      <span className="text-[10px] mt-1">Dashboard</span>
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/stock-movement");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <ClipboardDocumentListIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">Stock MV</span>
                  </button>
                  {isSuper && (
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate("/products");
                      }}
                      className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                    >
                      <BuildingStorefrontIcon className="w-10 h-10 text-[#069494]" />
                      <span className="text-[10px] mt-1">Products</span>
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/suppliers");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <UsersIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">Suppliers</span>
                  </button>
                </div>
              )}
              {activeMobileSection === "reports" && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setTxnsOpen(true);
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <ClipboardDocumentListIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">TXNs</span>
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setSalesOpen(true);
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <ShoppingCartIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">Sales</span>
                  </button>
                  {(isPower || isSuper) && (
                    <>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setSmvsOpen(true);
                        }}
                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                      >
                        <ClipboardDocumentListIcon className="w-10 h-10 text-[#069494]" />
                        <span className="text-[10px] mt-1">SMVs</span>
                      </button>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setPordersOpen(true);
                        }}
                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                      >
                        <ShoppingCartIcon className="w-10 h-10 text-[#069494]" />
                        <span className="text-[10px] mt-1">P-Orders</span>
                      </button>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setPvouchersOpen(true);
                        }}
                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                      >
                        <CurrencyDollarIcon className="w-10 h-10 text-[#069494]" />
                        <span className="text-[10px] mt-1">P-Vouchers</span>
                      </button>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setInvoicesOpen(true);
                        }}
                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                      >
                        <CurrencyDollarIcon className="w-10 h-10 text-[#069494]" />
                        <span className="text-[10px] mt-1">Invoices</span>
                      </button>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setRvouchersOpen(true);
                        }}
                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                      >
                        <CurrencyDollarIcon className="w-10 h-10 text-[#069494]" />
                        <span className="text-[10px] mt-1">R-Vouchers</span>
                      </button>
                    </>
                  )}
                </div>
              )}

              {activeMobileSection === "finance" && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/purchase-orders");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <ShoppingCartIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">P-Orders</span>
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/pay-voucher");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <CurrencyDollarIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">Pay Voucher</span>
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/invoices");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <CurrencyDollarIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">Invoices</span>
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/receipt-voucher");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <CurrencyDollarIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">R-Voucher</span>
                  </button>
                </div>
              )}
              {activeMobileSection === "settings" && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/users");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <UsersIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">Users</span>
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/promotions");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <CalendarDaysIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">Promotions</span>
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/accounting-period");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <CalendarDaysIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">A/C Period</span>
                  </button>
                  {isPower && isStaff && (
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate("/items-crud");
                      }}
                      className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                    >
                      <Squares2X2Icon className="w-10 h-10 text-[#069494]" />
                      <span className="text-[10px] mt-1">Items</span>
                    </button>
                  )}
                  {isSuper && isStaff && (
                    <>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate("/models-crud");
                        }}
                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                      >
                        <Squares2X2Icon className="w-10 h-10 text-[#069494]" />
                        <span className="text-[10px] mt-1">Models</span>
                      </button>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate("/categories-crud");
                        }}
                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                      >
                        <Squares2X2Icon className="w-10 h-10 text-[#069494]" />
                        <span className="text-[10px] mt-1">Categories</span>
                      </button>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate("/collections-crud");
                        }}
                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                      >
                        <Squares2X2Icon className="w-10 h-10 text-[#069494]" />
                        <span className="text-[10px] mt-1">Collections</span>
                      </button>
                    </>
                  )}
                  {isSuper && (
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate("/brands-crud");
                      }}
                      className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                    >
                      <Squares2X2Icon className="w-10 h-10 text-[#069494]" />
                      <span className="text-[10px] mt-1">Brands</span>
                    </button>
                  )}
                </div>
              )}
              {activeMobileSection === "account" && (
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/email-change");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <EnvelopeIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">Email Change</span>
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/password-change");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <KeyIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">Password Change</span>
                  </button>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/code-change");
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100"
                  >
                    <EllipsisHorizontalCircleIcon className="w-10 h-10 text-[#069494]" />
                    <span className="text-[10px] mt-1">PIN Change</span>
                  </button>
                  <button
                    onClick={() => {
                      setLogoutDialogOpen(true);
                      setMobileMenuOpen(false);
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100 text-red-600"
                  >
                    <ArrowLeftOnRectangleIcon className="w-10 h-10 text-red-600" />
                    <span className="text-[10px] mt-1">Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
// The SMVs modal is rendered above so it can be toggled from the Reports menu.
